import dayjs from 'dayjs';

import { config } from './config';

/** Marketing attribute filters each workbook dashboard shows, in the workbook's order. */
export const viewAttributes: Record<string, Array<string>> = {
  Performance: [
    'traffic_channel',
    'traffic_channel_detail',
    'country',
    'account',
    'campaign_segment',
    'campaign_segment_perf_marketing',
    'campaign_name',
    'campaign_bk',
    'adgroup_name',
    'adgroup_bk',
    'publisher_platform',
    'platform_position',
    'marketing_router_id',
    'marketing_router_variant_id',
    'campaign_status',
    'channel',
    'effective_target_roas_source',
  ],
  PoP: [
    'traffic_channel',
    'traffic_channel_detail',
    'campaign_segment',
    'campaign_name',
    'campaign_bk',
    'account',
    'country',
  ],
  Sales: [
    'geo_continent',
    'geo_country_name',
    'geo_region_name',
    'device_category',
    'customer_type',
    'signup_tenure_band',
    'luxplus_customer_flag',
    'traffic_channel',
    'traffic_channel_detail',
    'mkt_source',
    'mkt_medium',
    'mkt_campaign',
    'mkt_content',
    'affiliate_partner',
    'promo_applied',
    'bod_sales',
    'mweb_sales',
    'sale_type',
    'order_currency',
    'country_name',
    'order_with_insurance',
    'order_with_flight',
    'order_with_experience',
    'order_with_car_hire',
    'item_status',
    'brand_name',
    'category_name',
    'product_name',
    'vendor_name',
    'offer_name',
    'offer_country_name',
    'offer_geo_area_level_1',
    'offer_bk',
    'transaction_weekday',
  ],
  Monthly: [
    'account',
    'campaign_bk',
    'campaign_name',
    'country',
    'traffic_channel',
    'traffic_channel_detail',
    'campaign_segment',
    'adgroup_name',
  ],
};
viewAttributes.Attribution = viewAttributes.Performance;
viewAttributes['Product mix'] = viewAttributes.Performance;
viewAttributes.Sales.push(
  ...config.viewFilters.sales.filter(
    (key) =>
      !viewAttributes.Sales.includes(key) &&
      !['data_split_1', 'data_split_2', 'date_grain', 'attribution_model'].includes(key) &&
      !/_(from|to)$/.test(key),
  ),
);

const channelKeys = ['traffic_channel', 'traffic_channel_detail'];

/** Sales names channel details differently from the media source, so the Sales view keeps its own channel selections. */
export function attributeParam(view: string, key: string) {
  return view === 'Sales' && channelKeys.includes(key) ? `sales.${key}` : key;
}

/** The workbook's Data Split 1 / Data Split 2 parameter members for the media views. */
export const mediaSplits = [
  'All',
  'traffic_channel',
  'traffic_channel_detail',
  'country',
  'campaign_segment',
  'campaign_name',
  'campaign_bk',
  'account',
  'adgroup_name',
  'adgroup_bk',
  'publisher_platform',
  'platform_position',
  'campaign_segment_perf_marketing',
  'marketing_router_variant_id',
  'marketing_router_id',
];

export const attributeLabels: Record<string, string> = {
  campaign_bk: 'Campaign ID',
  adgroup_bk: 'Adgroup ID',
  adgroup_name: 'Adgroup Name',
  campaign_name: 'Campaign Name',
  account: 'Account',
  campaign_status: 'Campaign Status',
  channel: 'Channel',
  effective_target_roas_source: 'Effective Target ROAS Source',
  publisher_platform: 'Publisher Platform',
  platform_position: 'Platform Position',
  marketing_router_id: 'Router ID',
  marketing_router_variant_id: 'Variant',
  geo_continent: 'User Continent',
  geo_country_name: 'User Country',
  geo_region_name: 'User Region',
  device_category: 'User Device Category',
  customer_type: 'Customer Type',
  signup_tenure_band: 'Signup Tenure Band',
  luxplus_customer_flag: 'LP+ Subscription',
  mkt_source: 'Mkt Source',
  mkt_medium: 'Mkt Medium',
  mkt_campaign: 'Mkt Campaign',
  mkt_content: 'Mkt Content',
  affiliate_partner: 'Affiliate Partner',
  promo_applied: 'Promo Applied',
  bod_sales: 'BoD',
  mweb_sales: 'MWEB',
  sale_type: 'Sale Type',
  order_currency: 'Order Currency',
  country_name: 'Website Region',
  order_with_insurance: 'Order With Insurance',
  order_with_flight: 'Order With Flight',
  order_with_experience: 'Order With Experience',
  order_with_car_hire: 'Order With Car Hire',
  item_status: 'Item Status',
  brand_name: 'Brand Name',
  category_name: 'Category Name',
  product_name: 'Product Name',
  vendor_name: 'Vendor Name',
  offer_name: 'Offer Name',
  offer_country_name: 'Offer Country',
  offer_geo_area_level_1: 'Offer Geo Area Level 1',
  offer_bk: 'Offer Bk',
  transaction_weekday: 'Weekday',
};

/** Single-value aliases written by earlier report versions, read as one-member selections. */
export const legacyAttributes: Record<string, string> = {
  country: 'country',
  channel: 'traffic_channel',
  detail: 'traffic_channel_detail',
  campaignGroup: 'campaign_segment',
  segment: 'campaign_segment_perf_marketing',
  router: 'marketing_router_id',
  variant: 'marketing_router_variant_id',
};

/** The workbook's Metric | Sales and Metric parameter members, keyed by the native source field. */
export const salesMetrics: Array<[string, string]> = [
  ['sales_gm_ar', 'GM (AR)'],
  ['sales_ttv_ar', 'TTV (AR)'],
  ['gm_br', 'GM (BR)'],
  ['ttv_br', 'TTV (BR)'],
  ['sales_aom_br', 'AOM (BR)'],
  ['sales_aov_br', 'AOV (BR)'],
  ['orders_br', 'Orders (BR)'],
  ['customers_br', 'Customers (BR)'],
  ['sales_margin_rate_br', 'GM% (BR)'],
  ['sales_refund_rate', 'Refunds%'],
  ['refund_gm', 'Refunds (GM)'],
  ['refund_ttv', 'Refunds (TTV)'],
];
export const mediaMetrics: Array<[string, string]> = [
  ['cost', 'Costs'],
  ['clicks', 'Clicks'],
  ['cpc', 'CPC'],
  ['ctr', 'CTR'],
  ['sessions', 'Sessions'],
  ['sessions_per_click', 'Sessions/Clicks'],
  ['margin_roas', 'ROAS (Margin) (BR)'],
  ['ttv_roas', 'ROAS (TTV) (BR)'],
  ['gm', 'GM (BR)'],
  ['gm_ar', 'GM (AR)'],
  ['ttv', 'TTV (BR)'],
  ['ttv_ar', 'TTV (AR)'],
  ['margin_roas_ar', 'ROAS (Margin) (AR)'],
  ['ttv_roas_ar', 'ROAS (TTV) (AR)'],
  ['sign_ups', 'Sign-Ups'],
  ['cost_per_signup', 'Cost per Sign-Up'],
  ['orders', 'Orders'],
  ['session_conversion', 'CR (Orders/Sessions)'],
  ['customer_discount', 'Customer Discounts (BR) (Promo Excl Cancel)'],
  ['customer_discount_ar', 'Customer Discounts (AR) (Promo Excl Cancel)'],
  ['gm_after_discount', 'GM (BR) After Discounts'],
  ['gm_ar_after_discount', 'GM (AR) After Discounts'],
  ['roas_after_discount', 'ROAS (GM (BR)) After Customer Discount'],
  ['roas_ar_after_discount', 'ROAS (GM (AR)) After Customer Discount'],
];
const percentMetrics = ['ctr', 'session_conversion', 'sales_margin_rate_br', 'sales_refund_rate'];
const countMetrics = ['clicks', 'sessions', 'sessions_per_click', 'sign_ups', 'orders', 'orders_br', 'customers_br'];
export function metricFormat(key: string) {
  return percentMetrics.includes(key)
    ? 'percent'
    : countMetrics.includes(key)
      ? 'number'
      : key.includes('roas')
        ? 'ratio'
        : 'money';
}
export function metricLabel(key: string) {
  return [...salesMetrics, ...mediaMetrics].find(([metric]) => metric === key)?.[1] ?? key;
}

export const comparisonOptions: Array<[string, string]> = [
  ['previous', 'Previous period (same number of days)'],
  ['28days', 'Previous 28 days (only choose the CP end date)'],
  ['year', 'Last year (same dates)'],
  ['custom', 'Custom (choose start and end dates)'],
];

/** The 28-day comparison ignores the chosen start and reads the 28 days ending on the chosen end. */
export function currentPeriod(from: string, to: string, comparison: string) {
  return comparison === '28days' ? { from: dayjs(to).subtract(27, 'day').format('YYYY-MM-DD'), to } : { from, to };
}

/** Sales filters read the Sales source's own values; media filters never list Sales-only values. */
export function attributeChoices(
  { options, totals }: { options: Record<string, Array<string>>; totals: Record<string, number> },
  key: string,
  view: string,
) {
  const kind = view === 'Sales' && options[`sales.${key}`] ? `sales.${key}` : key;
  return { values: options[kind] ?? [], total: totals[kind] };
}
